import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { register, registerOAuth, login } from '../api';
import { useAuth } from '../context/AuthContext';
import { FaGoogle, FaMicrosoft, FaEye, FaEyeSlash } from 'react-icons/fa';
import { signInWithGoogle, signInWithMicrosoft, OAuthUser } from '../utils/oauth';

const Register: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState<boolean>(window.innerWidth >= 768 && window.innerWidth < 1024);
  const [viewportHeight, setViewportHeight] = useState<number>(window.innerHeight);
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();
  const isRegistrationEnabled = process.env.REACT_APP_ENABLE_REGISTRATION === 'true';

  // Calculate content level based on available space
  const getContentLevel = () => {
    if (!isMobile) return 'full';
    if (viewportHeight >= 900) return 'full'; // Very large mobile screens - show everything
    if (viewportHeight >= 800) return 'extended'; // Large mobile screens - show CTA only
    if (viewportHeight >= 700) return 'standard'; // Medium mobile screens - 4 bullets
    return 'minimal'; // Small mobile screens - 3 bullets
  };

  const contentLevel = getContentLevel();

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
      setViewportHeight(window.innerHeight);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate form
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!email || !password) {
      setError('Email and password are required');
      return;
    }

    if (!organizationName.trim()) {
      setError('Organization name is required');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setIsLoading(true);

    try {
      await register({
        email,
        password,
        firstName: firstName || undefined,
        lastName: lastName || undefined,
        organizationName: organizationName.trim()
      });

      // Automatically log the user in after successful registration
      const loginData = await login(email, password);

      // Update auth context
      authLogin(loginData.token, loginData.user);

      // Redirect to dashboard
      navigate('/overview');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setIsLoading(true);
    setError('');

    try {
      const googleUser: OAuthUser = await signInWithGoogle();

      // Register the user with OAuth
      const response = await registerOAuth({
        email: googleUser.email,
        firstName: googleUser.firstName,
        lastName: googleUser.lastName,
        oauthProvider: googleUser.provider,
        oauthProviderUserId: googleUser.providerId,
        organizationName: organizationName
      });

      // Store the token
      localStorage.setItem('token', response.token);
      // Update auth context with token and user data
      authLogin(response.token, response.user);

      // Navigate to dashboard
      navigate('/overview');
    } catch (err) {
      console.error('Google registration error:', err);
      setError(err instanceof Error ? err.message : 'Google registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMicrosoftSignUp = async () => {
    setIsLoading(true);
    setError('');

    try {
      const microsoftUser: OAuthUser = await signInWithMicrosoft();

      // Register the user with OAuth
      const response = await registerOAuth({
        email: microsoftUser.email,
        firstName: microsoftUser.firstName,
        lastName: microsoftUser.lastName,
        oauthProvider: microsoftUser.provider,
        oauthProviderUserId: microsoftUser.providerId,
        organizationName: organizationName
      });

      // Store the token
      localStorage.setItem('token', response.token);
      // Update auth context with token and user data
      authLogin(response.token, response.user);

      // Navigate to dashboard
      navigate('/overview');
    } catch (err) {
      console.error('Microsoft registration error:', err);
      setError(err instanceof Error ? err.message : 'Microsoft registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      background: 'linear-gradient(115deg, rgb(34, 34, 69) 25%, rgb(-77, 182, 159) 162%)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: isMobile ? 'flex-start' : 'center',
      padding: isMobile ? '0' : '20px 0'
    }}>
      <div
        style={{
          display: 'flex',
          width: '100%',
          maxWidth: '1200px',
          minHeight: '100vh',
          flexDirection: isMobile ? 'column' : 'row',
          overflow: isMobile ? 'auto' : 'visible'
        }}
        className={isMobile ? 'mobile-scrollable mobile-force-scroll' : ''}
      >
        <style>
          {`
          @keyframes slideInFromRight {
            from {
              transform: translateX(30px);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
          
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          .form-container {
            animation: slideInFromRight 0.6s ease-out;
          }
          
          .left-panel-content {
            animation: fadeIn 0.6s ease-out 0.2s both;
          }

          @media (max-width: 767px) {
            .mobile-stack {
              flex-direction: column !important;
            }
            .mobile-hide {
              display: none !important;
            }
            .mobile-compact {
              padding: 20px !important;
              
            }
            .mobile-text-small {
              font-size: 14px !important;
            }
            .mobile-text-medium {
              font-size: 16px !important;
            }
            .mobile-text-large {
              font-size: 24px !important;
            }
            .mobile-scrollable {
              overflow-y: auto !important;
              -webkit-overflow-scrolling: touch !important;
            }
          }

          @media (max-width: 767px) and (max-height: 600px) {
            .mobile-force-scroll {
              height: auto !important;
              min-height: auto !important;
              max-height: none !important;
            }
          }

          @media (max-width: 1024px) {
            .tablet-reduce {
              padding: 30px !important;
            }
            .tablet-text {
              font-size: 14px !important;
            }
          }
        `}
        </style>
        {/* Left side - Information panel */}
        <div style={{
          flex: isMobile ? 'none' : 1,
          minHeight: isMobile ? 'auto' : 'auto',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: isMobile ?
            (contentLevel === 'full' ? '20px 20px 12px 20px' :
              contentLevel === 'extended' ? '18px 20px 10px 20px' :
                contentLevel === 'standard' ? '12px 20px 6px 20px' : '8px 20px 2px 20px')
            : isTablet ? '40px 30px' : '60px 40px',
          color: 'white',
          position: 'relative'
        }}
          className={isMobile ? 'mobile-compact' : ''}
        >
          <div className="left-panel-content" style={{
            maxWidth: isMobile ? '100%' : '400px',
            textAlign: 'center'
          }}>
            {/* Logo */}
            <div style={{
              marginBottom: isMobile ?
                (contentLevel === 'full' ? '12px' :
                  contentLevel === 'extended' ? '10px' :
                    contentLevel === 'standard' ? '6px' : '4px')
                : '40px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: isMobile ? '0 auto 22px' : '0 auto 16px'
              }}>
                <a href="https://execdash.ai" style={{ textDecoration: 'none' }}>
                  <img
                    src="/execdash_logo_trans.png"
                    alt="ExecDash Logo"
                    style={{
                      height: isMobile ?'68px' : '80px',
                      width: 'auto',
                      filter: 'brightness(0) invert(1)', // Make logo white for dark background
                      cursor: 'pointer'
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </a>
              </div>

            </div>

            {/* Features list - Dynamic based on content level */}
            {isMobile ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px',
                textAlign: 'center',
                marginBottom: contentLevel === 'full' ? '12px' :
                  contentLevel === 'extended' ? '10px' :
                    contentLevel === 'standard' ? '6px' : '4px'
              }}>
                {/* Bullet Point 1 - Always shown */}
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                  marginBottom: contentLevel === 'full' ? '6px' :
                    contentLevel === 'extended' ? '5px' :
                      contentLevel === 'standard' ? '3px' : '2px',
                  width: '100%'
                }}>
                  <div style={{
                    width: contentLevel === 'full' ? '18px' :
                      contentLevel === 'extended' ? '16px' :
                        contentLevel === 'standard' ? '15px' : '14px',
                    height: contentLevel === 'full' ? '18px' :
                      contentLevel === 'extended' ? '16px' :
                        contentLevel === 'standard' ? '15px' : '14px',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: contentLevel === 'full' ? '10px' :
                      contentLevel === 'extended' ? '8px' :
                        contentLevel === 'standard' ? '7px' : '6px',
                    flexShrink: 0,
                    marginTop: '2px'
                  }}>
                    <span style={{
                      fontSize: contentLevel === 'full' ? '9px' :
                        contentLevel === 'extended' ? '8px' :
                          contentLevel === 'standard' ? '7px' : '7px'
                    }}>✓</span>
                  </div>
                  <span style={{
                    fontSize: contentLevel === 'full' ? '14px' :
                      contentLevel === 'extended' ? '13px' :
                        contentLevel === 'standard' ? '13px' : '12px',
                    lineHeight: contentLevel === 'full' ? '1.4' :
                      contentLevel === 'extended' ? '1.3' :
                        contentLevel === 'standard' ? '1.25' : '1.2',
                    fontWeight: 500,
                    flex: 1,
                    textAlign: 'center'
                  }}>Streamline your next leadership meeting with targeted priorities
                  </span>
                </div>

                {/* Bullet Point 2 - Always shown */}
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                  marginBottom: contentLevel === 'full' ? '6px' :
                    contentLevel === 'extended' ? '5px' :
                      contentLevel === 'standard' ? '3px' : '2px',
                  width: '100%'
                }}>
                  <div style={{
                    width: contentLevel === 'full' ? '18px' :
                      contentLevel === 'extended' ? '16px' :
                        contentLevel === 'standard' ? '15px' : '14px',
                    height: contentLevel === 'full' ? '18px' :
                      contentLevel === 'extended' ? '16px' :
                        contentLevel === 'standard' ? '15px' : '14px',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: contentLevel === 'full' ? '10px' :
                      contentLevel === 'extended' ? '8px' :
                        contentLevel === 'standard' ? '7px' : '6px',
                    flexShrink: 0,
                    marginTop: '2px'
                  }}>
                    <span style={{
                      fontSize: contentLevel === 'full' ? '9px' :
                        contentLevel === 'extended' ? '8px' :
                          contentLevel === 'standard' ? '7px' : '7px'
                    }}>✓</span>
                  </div>
                  <span style={{
                    fontSize: contentLevel === 'full' ? '14px' :
                      contentLevel === 'extended' ? '13px' :
                        contentLevel === 'standard' ? '13px' : '12px',
                    lineHeight: contentLevel === 'full' ? '1.4' :
                      contentLevel === 'extended' ? '1.3' :
                        contentLevel === 'standard' ? '1.25' : '1.2',
                    fontWeight: 500,
                    flex: 1,
                    textAlign: 'center'
                  }}>
                    Unify business data into a single executive view
                  </span>
                </div>

                {/* Bullet Point 3 - Always shown */}
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                  marginBottom: contentLevel === 'full' ? '6px' :
                    contentLevel === 'extended' ? '5px' :
                      contentLevel === 'standard' ? '3px' : '2px',
                  width: '100%'
                }}>
                  <div style={{
                    width: contentLevel === 'full' ? '18px' :
                      contentLevel === 'extended' ? '16px' :
                        contentLevel === 'standard' ? '15px' : '14px',
                    height: contentLevel === 'full' ? '18px' :
                      contentLevel === 'extended' ? '16px' :
                        contentLevel === 'standard' ? '15px' : '14px',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: contentLevel === 'full' ? '10px' :
                      contentLevel === 'extended' ? '8px' :
                        contentLevel === 'standard' ? '7px' : '6px',
                    flexShrink: 0,
                    marginTop: '2px'
                  }}>
                    <span style={{
                      fontSize: contentLevel === 'full' ? '9px' :
                        contentLevel === 'extended' ? '8px' :
                          contentLevel === 'standard' ? '7px' : '7px'
                    }}>✓</span>
                  </div>
                  <span style={{
                    fontSize: contentLevel === 'full' ? '14px' :
                      contentLevel === 'extended' ? '13px' :
                        contentLevel === 'standard' ? '13px' : '12px',
                    lineHeight: contentLevel === 'full' ? '1.4' :
                      contentLevel === 'extended' ? '1.3' :
                        contentLevel === 'standard' ? '1.25' : '1.2',
                    fontWeight: 500,
                    flex: 1,
                    textAlign: 'center'
                  }}>
                    Surface key trends, risks, and opportunities with AI-augmented insights
                  </span>
                </div>

                {/* Bullet Point 4 - Shown on standard and above */}
                {(contentLevel === 'full' || contentLevel === 'extended' || contentLevel === 'standard') && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-start',
                    marginBottom: contentLevel === 'full' ? '6px' :
                      contentLevel === 'extended' ? '5px' : '3px',
                    width: '100%'
                  }}>
                    <div style={{
                      width: contentLevel === 'full' ? '18px' :
                        contentLevel === 'extended' ? '16px' : '15px',
                      height: contentLevel === 'full' ? '18px' :
                        contentLevel === 'extended' ? '16px' : '15px',
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: contentLevel === 'full' ? '10px' :
                        contentLevel === 'extended' ? '8px' : '7px',
                      flexShrink: 0,
                      marginTop: '2px'
                    }}>
                      <span style={{
                        fontSize: contentLevel === 'full' ? '9px' :
                          contentLevel === 'extended' ? '8px' : '7px'
                      }}>✓</span>
                    </div>
                    <span style={{
                      fontSize: contentLevel === 'full' ? '14px' :
                        contentLevel === 'extended' ? '13px' : '13px',
                      lineHeight: contentLevel === 'full' ? '1.4' :
                        contentLevel === 'extended' ? '1.3' : '1.25',
                      fontWeight: 500,
                      flex: 1,
                      textAlign: 'center'
                    }}>
                      Get started in minutes with smart defaults and role-specific views
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ textAlign: 'left', marginBottom: '40px' }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '16px',
                    flexShrink: 0
                  }}>
                    <span style={{ fontSize: '12px' }}>✓</span>
                  </div>
                  <span style={{ fontSize: '16px', lineHeight: '1.5' }}>
                    Streamline your next leadership meeting with targeted priorities    
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '16px',
                    flexShrink: 0
                  }}>
                    <span style={{ fontSize: '12px' }}>✓</span>
                  </div>
                  <span style={{ fontSize: '16px', lineHeight: '1.5' }}>
                    Unify business data into a single executive view
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '16px',
                    flexShrink: 0
                  }}>
                    <span style={{ fontSize: '12px' }}>✓</span>
                  </div>
                  <span style={{ fontSize: '16px', lineHeight: '1.5' }}>
                    Surface key trends, risks, and opportunities with AI-augmented insights
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '16px',
                    flexShrink: 0
                  }}>
                    <span style={{ fontSize: '12px' }}>✓</span>
                  </div>
                  <span style={{ fontSize: '16px', lineHeight: '1.5' }}>
                    Get started in minutes with smart defaults and role-specific views
                  </span>
                </div>
              </div>
            )}



            {/* Trust indicators - Show on full content level only */}
            {((!isMobile) || (isMobile && (contentLevel === 'full' || contentLevel === 'standard' || contentLevel === 'extended'))) && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: isMobile ? '26px' : '24px',
                opacity: 0.8,
                marginTop: isMobile ? '20px' : '0'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    fontSize: isMobile ? '10px' : '12px',
                    marginBottom: '4px'
                  }}>Built</div>
                  <div style={{
                    fontSize: isMobile ? '12px' : '14px',
                    fontWeight: 600
                  }}>For Leaders</div>
                  <div style={{
                    fontSize: isMobile ? '12px' : '14px',
                    fontWeight: 600
                  }}>By Leaders</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    fontSize: isMobile ? '10px' : '12px',
                    marginBottom: '4px'
                  }}>Exec Rating</div>
                  <div style={{
                    fontSize: isMobile ? '12px' : '14px',
                    fontWeight: 600
                  }}>4.8/5</div>
                  <div style={{
                    fontSize: isMobile ? '10px' : '12px'
                  }}>★★★★★</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    fontSize: isMobile ? '10px' : '12px',
                    marginBottom: '4px'
                  }}>Uptime</div>
                  <div style={{
                    fontSize: isMobile ? '12px' : '14px',
                    fontWeight: 600
                  }}>99.9%</div>
                  <div style={{
                    fontSize: isMobile ? '10px' : '12px'
                  }}>Guaranteed</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right side - Registration form */}
        <div style={{
          flex: isMobile ? 0 : 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: isMobile ?
            (contentLevel === 'full' ? '6px 16px 12px 16px' :
              contentLevel === 'extended' ? '5px 16px 10px 16px' :
                contentLevel === 'standard' ? '3px 16px 6px 16px' : '2px 16px 4px 16px')
            : isTablet ? '30px' : '40px'
        }}
          className={isMobile ? 'mobile-compact' : isTablet ? 'tablet-reduce' : ''}
        >
          <div className="form-container" style={{
            width: '100%',
            maxWidth: isMobile ? '100%' : '400px',
            backgroundColor: 'white',
            padding: isMobile ?
              (contentLevel === 'full' ? '12px' :
                contentLevel === 'extended' ? '11px' :
                  contentLevel === 'standard' ? '10px' : '8px')
              : isTablet ? '36px' : '48px',
            borderRadius: isMobile ? '6px' : '12px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            margin: isMobile ? '0' : '0'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <h2 style={{
                fontSize: '30px',
                fontWeight: 300,
                color: '#1a202c',
                margin: '0 0 8px 0',
                fontFamily: 'Raleway, sans-serif',
              }}>
                {currentStep === 1 ? "Let's get started!" : "Create your account"}
              </h2>
              <p style={{ color: '#718096', margin: 0, fontSize: '14px' }}>
                {currentStep === 1
                  ? 'You are minutes away from turning noise into insight'
                  : 'Choose how you\'d like to sign up'
                }
              </p>
            </div>

            {currentStep === 1 ? (
              // Step 1: Organization Name
              <>
                <div style={{ marginBottom: '24px' }}>
                  <label htmlFor="organizationName" style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#374151'
                  }}>
                    Organization Name *
                  </label>
                  <input
                    id="organizationName"
                    type="text"
                    value={organizationName}
                    onChange={(e) => setOrganizationName(e.target.value)}
                    placeholder="Enter your organization name"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: '1px solid #d1d5db',
                      fontSize: '14px',
                      transition: 'border-color 0.2s',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && organizationName.trim()) {
                        setCurrentStep(2);
                      }
                    }}
                    required
                    autoFocus
                  />
                </div>

                <button
                  type="button"
                  onClick={() => setCurrentStep(2)}
                  disabled={!organizationName.trim() || !isRegistrationEnabled}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    background: !organizationName.trim() ? '#9ca3af' : 'linear-gradient(115deg, rgb(34, 34, 69) 25%, rgb(-77, 182, 159) 162%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: 600,
                    cursor: !organizationName.trim() ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s',
                    marginBottom: '16px'
                  }}
                  onMouseOver={(e) => {
                    if (organizationName.trim()) {
                      e.currentTarget.style.backgroundColor = '#2563eb';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (organizationName.trim()) {
                      e.currentTarget.style.backgroundColor = '#3b82f6';
                    }
                  }}
                >
                  Continue
                </button>
                {!isRegistrationEnabled && (
                  <div style={{
                    padding: '12px',
                    borderRadius: '4px',
                    backgroundColor: '#fff3cd',
                    color: '#856404',
                    marginBottom: isMobile ?
                      (contentLevel === 'full' ? '16px' :
                        contentLevel === 'extended' ? '14px' :
                          contentLevel === 'standard' ? '10px' : '8px')
                      : '24px',
                    textAlign: 'center',
                    border: '1px solid #ffeaa7'
                  }}>
                    Registration is currently disabled. Sorry for the inconvenience.
                  </div>
                )}
                {/* Sign in link */}
                <div style={{ textAlign: 'center' }}>
                  <span style={{ fontSize: '14px', color: '#6b7280' }}>
                    Already have an account?{' '}
                    <Link
                      to="/login"
                      style={{
                        color: '#3b82f6',
                        textDecoration: 'none',
                        fontWeight: 500
                      }}
                    >
                      Sign in
                    </Link>
                  </span>
                </div>
              </>
            ) : (

              // Step 2: Sign-up Options
              <>
                {/* Back button */}
                <div style={{ marginBottom: '10px' }}>
                  <button
                    type="button"
                    onClick={() => setCurrentStep(1)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      background: 'none',
                      border: 'none',
                      color: '#6b7280',
                      fontSize: '18px',
                      cursor: 'pointer',
                      padding: '4px 0'
                    }}
                  >
                    ← {organizationName}
                  </button>
                </div>

               

                {/* OAuth buttons */}
                <div style={{ marginBottom: '24px' }}>
                  <button
                    type="button"
                    onClick={handleGoogleSignUp}
                    disabled={isLoading || !isRegistrationEnabled}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      marginBottom: '12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      backgroundColor: 'white',
                      color: '#4a5568',
                      fontSize: '14px',
                      fontWeight: 500,
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => {
                      if (!isLoading) {
                        e.currentTarget.style.backgroundColor = '#f7fafc';
                        e.currentTarget.style.borderColor = '#cbd5e0';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (!isLoading) {
                        e.currentTarget.style.backgroundColor = 'white';
                        e.currentTarget.style.borderColor = '#e2e8f0';
                      }
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    {isLoading ? 'Signing up...' : 'Sign up with Google'}
                  </button>

                  <button
                    type="button"
                    onClick={handleMicrosoftSignUp}
                    disabled={isLoading || !isRegistrationEnabled}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      backgroundColor: 'white',
                      color: '#4a5568',
                      fontSize: '14px',
                      fontWeight: 500,
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => {
                      if (!isLoading) {
                        e.currentTarget.style.backgroundColor = '#f7fafc';
                        e.currentTarget.style.borderColor = '#cbd5e0';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (!isLoading) {
                        e.currentTarget.style.backgroundColor = 'white';
                        e.currentTarget.style.borderColor = '#e2e8f0';
                      }
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24">
                      <path fill="#f25022" d="M1 1h10v10H1z" />
                      <path fill="#00a4ef" d="M13 1h10v10H13z" />
                      <path fill="#7fba00" d="M1 13h10v10H1z" />
                      <path fill="#ffb900" d="M13 13h10v10H13z" />
                    </svg>
                    {isLoading ? 'Signing up...' : 'Sign up with Microsoft'}
                  </button>
                </div>

                {/* Divider */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '24px',
                  color: '#a0aec0',
                  fontSize: '14px'
                }}>
                  <div style={{ flex: 1, height: '1px', backgroundColor: '#e2e8f0' }}></div>
                  <span style={{ padding: '0 16px' }}>Or sign up with email</span>
                  <div style={{ flex: 1, height: '1px', backgroundColor: '#e2e8f0' }}></div>
                </div>



                <form onSubmit={handleSubmit}>
                  {/* Name fields */}
                  <div style={{ display: 'flex', gap: '14px', marginBottom: '20px' }}>
                    <div style={{ flex: 1 }}>
                      <label htmlFor="firstName" style={{
                        display: 'block',
                        marginBottom: '6px',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#374151'
                      }}>
                        First Name
                      </label>
                      <input
                        id="firstName"
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          borderRadius: '8px',
                          border: '1px solid #d1d5db',
                          fontSize: '14px',
                          transition: 'border-color 0.2s',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                        onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label htmlFor="lastName" style={{
                        display: 'block',
                        marginBottom: '6px',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#374151'
                      }}>
                        Last Name
                      </label>
                      <input
                        id="lastName"
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          borderRadius: '8px',
                          border: '1px solid #d1d5db',
                          fontSize: '14px',
                          transition: 'border-color 0.2s',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                        onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div style={{ marginBottom: '20px' }}>
                    <label htmlFor="email" style={{
                      display: 'block',
                      marginBottom: '6px',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#374151'
                    }}>
                      Email Address *
                    </label>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        border: '1px solid #d1d5db',
                        fontSize: '14px',
                        transition: 'border-color 0.2s',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                      required
                    />
                  </div>



                  {/* Password */}
                  <div style={{ marginBottom: '20px' }}>
                    <label htmlFor="password" style={{
                      display: 'block',
                      marginBottom: '6px',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#374151'
                    }}>
                      Password *
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '12px 40px 12px 16px',
                          borderRadius: '8px',
                          border: '1px solid #d1d5db',
                          fontSize: '14px',
                          transition: 'border-color 0.2s',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                        onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                        required
                      />
                      <button
                        type="button"
                        disabled={isLoading || !isRegistrationEnabled}
                        onClick={() => setShowPassword(!showPassword)}
                        style={{
                          position: 'absolute',
                          right: '12px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#6b7280',
                          fontSize: '14px',
                          padding: '4px'
                        }}
                      >
                        {showPassword ? '👁️' : '👁️‍🗨️'}
                      </button>
                      
                    </div>
                  </div>

                  {/* Confirm Password */}
                  <div style={{ marginBottom: '24px' }}>
                    <label htmlFor="confirmPassword" style={{
                      display: 'block',
                      marginBottom: '6px',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#374151'
                    }}>
                      Confirm Password *
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        id="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '12px 40px 12px 16px',
                          borderRadius: '8px',
                          border: '1px solid #d1d5db',
                          fontSize: '14px',
                          transition: 'border-color 0.2s',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                        onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        style={{
                          position: 'absolute',
                          right: '12px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#6b7280',
                          fontSize: '14px',
                          padding: '4px'
                        }}
                      >
                        {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                      </button>
                    </div>
                  </div>

                  {error && (
                    <div style={{
                      padding: '12px 16px',
                      borderRadius: '8px',
                      backgroundColor: '#fed7d7',
                      color: '#c53030',
                      marginBottom: '20px',
                      fontSize: '14px',
                      border: '1px solid #feb2b2'
                    }}>
                      {error}
                    </div>
                  )}

                  {/* Submit button */}
                  <button
                    type="submit"
                    disabled={isLoading}
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      background: isLoading ? '#9ca3af' : 'linear-gradient(115deg, rgb(34, 34, 69) 25%, rgb(77, 182, 159) 162%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: 600,
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      transition: 'background-color 0.2s',
                      marginBottom: '16px'
                    }}
                    onMouseOver={(e) => {
                      if (!isLoading) {
                        e.currentTarget.style.background = 'linear-gradient(115deg, rgb(34, 34, 69) 25%, rgb(77, 182, 159) 162%)';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (!isLoading) {
                        e.currentTarget.style.background = 'linear-gradient(115deg, rgb(34, 34, 69) 25%, rgb(77, 182, 159) 162%)';
                      }
                    }}
                  >
                    {isLoading ? 'Creating account...' : 'Create account'}
                  </button>

                  {/* Terms and Privacy */}
                  <p style={{
                    fontSize: '12px',
                    color: '#6b7280',
                    textAlign: 'center',
                    margin: '16px 0 0 0',
                    lineHeight: '1.5'
                  }}>
                    By creating an account, you agree to our{' '}
                    <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'none' }}>
                      Terms of Service
                    </a>
                    {' '}and{' '}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'none' }}>
                      Privacy Policy
                    </a>
                  </p>

                  {/* Sign in link */}
                  <div style={{ textAlign: 'center' }}>
                    <span style={{ fontSize: '14px', color: '#6b7280' }}>
                      Already have an account?{' '}
                      <Link
                        to="/login"
                        style={{
                          color: '#3b82f6',
                          textDecoration: 'none',
                          fontWeight: 500
                        }}
                      >
                        Sign in
                      </Link>
                    </span>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
